import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/api';
import SavingsDebtTrend from './SavingsDebtTrend';
import BudgetProgressCards from './BudgetProgressCards';
import debtService from '../services/debtService';
import { useLanguage } from '../contexts/LanguageContext';
import { formatAmount } from '../utils/formatHelpers';
import {
  getCurrencySymbol,
  detectDefaultCurrency,
} from '../utils/currencyUtils';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
  Alert,
} from '@mui/material';

/**
 * Simplified Dashboard component that displays bank accounts and debts
 * with minimal styling to avoid theme-related errors
 */
const NotionDashboard = () => {
  const user = useSelector(state => state.auth.user);
  const navigate = useNavigate();
  const { language } = useLanguage();

  // States for financial data
  const [accounts, setAccounts] = useState([]);
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 4 ago 2026 (pedido de Augusto): antes este panel convertía todo a una
  // sola "Moneda" elegida en un selector, usando tasas de cambio en vivo
  // -- confuso, porque mezclaba cuentas reales en soles con un total en
  // dólares que no correspondía a ningún saldo real (mismo problema ya
  // resuelto en Cuentas, ver CuentasPage.js). Se quita el selector y en
  // vez de un solo total "convertido" se muestran los saldos reales
  // agrupados en 2 columnas: moneda nacional y dólares -- sin inventar
  // una tasa de cambio.
  //
  // defaultCurrency puede fallar en detectar Perú y devolver 'USD' (ver
  // detectDefaultCurrency) -- monedaLocal corrige ese caso cayendo a la
  // moneda más común entre las cuentas/deudas reales del cliente.
  const defaultCurrency = useMemo(() => detectDefaultCurrency(user?.country), [user?.country]);
  const monedaLocal = useMemo(() => {
    if (defaultCurrency !== 'USD') return defaultCurrency;
    const conteo = {};
    [...accounts, ...debts].forEach((item) => {
      const m = item.moneda || item.currency;
      if (m && m !== 'USD') conteo[m] = (conteo[m] || 0) + 1;
    });
    const candidatas = Object.keys(conteo).sort((a, b) => conteo[b] - conteo[a]);
    return candidatas[0] || 'USD';
  }, [defaultCurrency, accounts, debts]);
  const monedaDolares = 'USD';

  const [currentDate, setCurrentDate] = useState(new Date());

  // Load financial data EXCLUSIVAMENTE desde la API (misma fuente de
  // verdad que Cuentas y Deudas). No leemos ni mezclamos localStorage
  // aquí: eso era lo que causaba que este panel mostrara cuentas/deudas
  // de sesiones de prueba viejas en vez de los datos reales del usuario.
  useEffect(() => {
    const loadFinancialData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [accountsRes, debtsRes] = await Promise.all([
          apiClient.get('/dashboard/accounts'),
          debtService.getAll(),
        ]);

        setAccounts(Array.isArray(accountsRes.data) ? accountsRes.data : []);
        setDebts(Array.isArray(debtsRes.data) ? debtsRes.data : []);
      } catch (e) {
        console.error('Error loading financial data:', e);
        setAccounts([]);
        setDebts([]);
        setError('No se pudieron cargar tus datos financieros. Intenta recargar la página.');
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      loadFinancialData();
    } else {
      setLoading(false);
    }
  }, [user]);

  // 5 ago 2026 (Augusto: "no incluyas las tarjetas de crédito como cuentas
  // bancarias en el Panel"): una tarjeta de crédito importada se guarda
  // como Account (tipo 'tarjeta_credito', para que aparezca en la página
  // Cuentas junto a sus consumos) Y ADEMÁS como Debt (ver
  // _find_or_create_debt en statements.py) -- acá se listaban las DOS
  // veces: una en "Mis Cuentas Bancarias" (desde `accounts`) y otra en
  // "Mis Deudas" (desde `debts`). Mismos tipos que TIPOS_DEUDA en
  // CuentasPage.js: se excluyen de `accounts` acá para que solo queden
  // cuentas bancarias de verdad; la tarjeta sigue viéndose una sola vez,
  // en Mis Deudas.
  const TIPOS_CUENTA_DEUDA = [
    'tarjeta_credito',
    'prestamo_personal',
    'prestamo_hipotecario',
    'prestamo_auto',
    'prestamo_educacion',
    'prestamo_proveedores',
  ];
  const cuentasBancarias = accounts.filter((a) => !TIPOS_CUENTA_DEUDA.includes(a.tipo));

  // Format accounts and debts for display
  const formattedAccounts = cuentasBancarias.map(account => {
    const originalBalance = typeof account.saldo === 'number'
      ? account.saldo
      : parseFloat(account.saldo ?? account.balance ?? '0') || 0;
    return {
      id: account.id || Math.random().toString(36).substr(2, 9),
      name: account.nombre || account.name || (language === 'es' ? 'Cuenta' : 'Account'),
      institution: account.entidad || account.institution || '',
      balance: formatAmount(originalBalance),
      currency: account.moneda || account.currency || monedaLocal || 'USD',
      originalBalance
    };
  });

  const formattedDebts = debts.map(debt => {
    const originalAmount = typeof debt.monto === 'number'
      ? debt.monto
      : parseFloat(debt.monto ?? debt.amount ?? '0') || 0;
    const originalPayment = typeof debt.pago_mensual === 'number'
      ? debt.pago_mensual
      : parseFloat(debt.pago_mensual ?? debt.monthlyPayment ?? '0') || 0;
    return {
      id: debt.id || Math.random().toString(36).substr(2, 9),
      name: debt.nombre || debt.name || (language === 'es' ? 'Deuda' : 'Debt'),
      institution: debt.entidad || debt.institution || '',
      // 4 ago 2026: identifica la tarjeta/deuda "de verdad" sin importar
      // la moneda -- una tarjeta con saldo en soles Y dólares crea 2
      // registros Debt (ver _find_or_create_debt en statements.py),
      // mismo nombre+entidad+ultimos_digitos, distinta moneda. Se usa
      // para agruparlos en una sola fila más abajo.
      identidad: `${debt.entidad || ''}|${debt.nombre || ''}|${debt.ultimos_digitos || ''}`,
      ultimosDigitos: debt.ultimos_digitos || '',
      amount: formatAmount(originalAmount),
      payment: formatAmount(originalPayment),
      currency: debt.moneda || debt.currency || monedaLocal || 'USD',
      originalAmount,
      originalPayment
    };
  });

  // Agrupa las deudas separadas por moneda (misma tarjeta, un registro
  // por moneda) en una sola fila con 2 columnas -- antes se veían como 2
  // líneas distintas ("American Express" repetido), confuso.
  const deudasAgrupadas = useMemo(() => {
    const map = new Map();
    formattedDebts.forEach((d) => {
      if (!map.has(d.identidad)) {
        map.set(d.identidad, { id: d.identidad, name: d.name, institution: d.institution, montos: {}, pagos: {} });
      }
      const g = map.get(d.identidad);
      g.montos[d.currency] = d.originalAmount;
      g.pagos[d.currency] = d.originalPayment;
    });
    return Array.from(map.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debts, language, monedaLocal]);

  // Suma real (sin conversión) de las cuentas/deudas que están en cada
  // una de las 2 columnas -- una cuenta en, por ejemplo, Libras no cae en
  // ninguna de las 2 y simplemente no se cuenta en el total (sigue
  // visible con su propio código en la lista de abajo).
  const sumaPorMoneda = (items, campoMonto, moneda) =>
    items.filter((i) => i.currency === moneda).reduce((total, i) => total + i[campoMonto], 0);

  const totalCuentasNacional = sumaPorMoneda(formattedAccounts, 'originalBalance', monedaLocal);
  const totalCuentasDolares = sumaPorMoneda(formattedAccounts, 'originalBalance', monedaDolares);
  const totalDeudaNacional = sumaPorMoneda(formattedDebts, 'originalAmount', monedaLocal);
  const totalDeudaDolares = sumaPorMoneda(formattedDebts, 'originalAmount', monedaDolares);

  // Format date to string
  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  };

  // 6 ago 2026 (Augusto: "mandar una alerta 2 semanas antes que venza la
  // deuda para que la persona decida si pagar el 100% o financiar, y que
  // el sistema le diga las cuotas y el costo de la deuda"): además del
  // correo diario (ver DEBT_DECISION_DAYS en backend_new/.../admin.py),
  // se muestra el mismo aviso acá para quien entra al Panel antes de que
  // llegue el correo. Misma fórmula de amortización que DebtsPage.js
  // (calcularCuotaMensual) y el mismo cálculo de "días hasta el
  // vencimiento" que _dias_hasta_vencimiento en admin.py, replicados acá
  // porque cada uno vive en su propio runtime (backend en Python, esto en
  // el navegador).
  const DEBT_DECISION_DAYS = 14;

  const diasHastaVencimiento = (diaVencimiento, hoy) => {
    const msPorDia = 1000 * 60 * 60 * 24;
    const hoySinHora = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    const ultimoDiaEsteMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).getDate();
    const diaEsteMes = Math.min(diaVencimiento, ultimoDiaEsteMes);
    const vencimientoEsteMes = new Date(hoy.getFullYear(), hoy.getMonth(), diaEsteMes);
    if (vencimientoEsteMes >= hoySinHora) {
      return Math.round((vencimientoEsteMes - hoySinHora) / msPorDia);
    }
    let nextMonth = hoy.getMonth() + 1;
    let nextYear = hoy.getFullYear();
    if (nextMonth > 11) { nextMonth = 0; nextYear += 1; }
    const ultimoDiaNextMonth = new Date(nextYear, nextMonth + 1, 0).getDate();
    const diaNextMonth = Math.min(diaVencimiento, ultimoDiaNextMonth);
    const vencimientoNextMonth = new Date(nextYear, nextMonth, diaNextMonth);
    return Math.round((vencimientoNextMonth - hoySinHora) / msPorDia);
  };

  const calcularCuotaMensualDash = (monto, tasaAnualPct, plazoMeses) => {
    const P = Number(monto) || 0;
    const n = parseInt(plazoMeses, 10) || 0;
    if (!P || !n) return null;
    const rMensual = (Number(tasaAnualPct) || 0) / 100 / 12;
    if (rMensual === 0) return P / n;
    const factor = Math.pow(1 + rMensual, -n);
    return (P * rMensual) / (1 - factor);
  };

  const alertasVencimiento = useMemo(() => {
    const hoy = new Date();
    return debts
      .filter((d) => d.dia_vencimiento != null)
      .map((d) => ({ debt: d, dias: diasHastaVencimiento(d.dia_vencimiento, hoy) }))
      .filter(({ dias }) => dias >= 0 && dias <= DEBT_DECISION_DAYS)
      .sort((a, b) => a.dias - b.dias);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debts]);

  return (
    // 28 ago 2026 (pedido de Augusto: "veo muchos espacios en blanco...
    // scroll up and down tedioso"): mismo problema que en
    // TransactionsPage.js -- este Container ya vive DENTRO del Container
    // de NotionLayout.js, así que mt:4/mb:4 (32px arriba Y abajo) se
    // sumaban al padding que el layout exterior ya pone. Se baja a algo
    // mínimo -- el Tablero es la pantalla que más se abre, así que es
    // donde más se nota ese scroll de más antes de ver contenido real.
    <Container maxWidth="lg" disableGutters sx={{ mt: 0, mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {language === 'es' ? 'Panel Financiero' : 'Financial Dashboard'}
        </Typography>
        <Typography variant="subtitle1">
          {formatDate(currentDate)}
        </Typography>
      </Box>

      {loading && (
        <Alert severity="info">
          {language === 'es' ? 'Cargando tus datos financieros...' : 'Loading your financial data...'}
        </Alert>
      )}

      {error && (
        <Alert severity="error">{error}</Alert>
      )}

      {!loading && alertasVencimiento.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {alertasVencimiento.map(({ debt, dias }) => {
            const cuota = calcularCuotaMensualDash(debt.monto, debt.tasa_interes, debt.plazo_meses);
            const costoFinanciamiento = cuota != null && debt.plazo_meses
              ? cuota * debt.plazo_meses - (Number(debt.monto) || 0)
              : null;
            const simbolo = getCurrencySymbol(debt.moneda);
            let cuando;
            if (dias === 0) cuando = language === 'es' ? 'hoy' : 'today';
            else if (dias === 1) cuando = language === 'es' ? 'mañana' : 'tomorrow';
            else cuando = language === 'es' ? `en ${dias} días` : `in ${dias} days`;

            return (
              <Alert key={debt.id} severity={dias <= 1 ? 'error' : 'warning'} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {debt.nombre}{debt.entidad ? ` (${debt.entidad})` : ''} {language === 'es' ? 'vence' : 'is due'} {cuando}
                </Typography>
                <Typography variant="body2">
                  {language === 'es' ? 'Pagar 100% ahora' : 'Pay 100% now'}: {simbolo}{formatAmount(debt.monto)}
                  {cuota != null && (
                    <>
                      {' · '}
                      {language === 'es' ? 'Financiar en' : 'Finance over'} {debt.plazo_meses} {language === 'es' ? 'cuotas de' : 'installments of'} {simbolo}{formatAmount(cuota)}
                      {costoFinanciamiento != null && (
                        <> ({language === 'es' ? 'costo de interés' : 'interest cost'}: {simbolo}{formatAmount(costoFinanciamiento)})</>
                      )}
                    </>
                  )}
                </Typography>
              </Alert>
            );
          })}
        </Box>
      )}

      {!loading && <SavingsDebtTrend />}

      <Grid container spacing={2}>
        {/* Bank Accounts Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title={language === 'es' ? 'Mis Cuentas Bancarias' : 'My Bank Accounts'}
              sx={{ pb: 1 }}
            />
            <Divider />
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              {/* 4 ago 2026 (pedido de Augusto): 2 columnas con el saldo
                  real en cada moneda -- sin convertir nada, en vez del
                  selector "Moneda" + total único de antes. */}
              <Box display="flex" gap={3} mb={1.5}>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    {language === 'es' ? 'Balance en moneda nacional' : 'Balance in local currency'}
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {getCurrencySymbol(monedaLocal)}{formatAmount(totalCuentasNacional)} {monedaLocal}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    {language === 'es' ? 'Balance en dólares' : 'Balance in dollars'}
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {getCurrencySymbol(monedaDolares)}{formatAmount(totalCuentasDolares)} {monedaDolares}
                  </Typography>
                </Box>
              </Box>
              <Divider />
              {formattedAccounts.length === 0 ? (
                <Typography color="textSecondary">
                  {language === 'es'
                    ? 'No se encontraron cuentas bancarias. Agrega cuentas en la sección Cuentas.'
                    : 'No bank accounts found. Add accounts in the bank accounts form.'}
                </Typography>
              ) : (
                <List disablePadding>
                  {/* 3 ago 2026 (pedido de Augusto): cada cuenta lleva a
                      /transactions ya filtrado por esa cuenta -- antes
                      esta lista era solo informativa, sin forma de ver
                      los movimientos de una cuenta en particular desde
                      acá. */}
                  {formattedAccounts.map(account => (
                    <ListItemButton
                      key={account.id}
                      divider
                      onClick={() => navigate(`/transactions?account_id=${account.id}`)}
                    >
                      <ListItemText
                        primary={account.name}
                        secondary={account.institution}
                      />
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {getCurrencySymbol(account.currency)}{account.balance} {account.currency}
                      </Typography>
                    </ListItemButton>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Debts Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title={language === 'es' ? 'Mis Deudas' : 'My Debts'}
              sx={{ pb: 1 }}
            />
            <Divider />
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" gap={3} mb={1.5}>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    {language === 'es' ? 'Deuda en moneda nacional' : 'Debt in local currency'}
                  </Typography>
                  <Typography variant="h6" color="error">
                    {getCurrencySymbol(monedaLocal)}{formatAmount(totalDeudaNacional)} {monedaLocal}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    {language === 'es' ? 'Deuda en dólares' : 'Debt in dollars'}
                  </Typography>
                  <Typography variant="h6" color="error">
                    {getCurrencySymbol(monedaDolares)}{formatAmount(totalDeudaDolares)} {monedaDolares}
                  </Typography>
                </Box>
              </Box>
              <Divider />
              {deudasAgrupadas.length === 0 ? (
                <Typography color="textSecondary">
                  {language === 'es'
                    ? 'No se encontraron deudas. Agrega deudas en la sección Deuda.'
                    : 'No debts found. Add debts in the debt form.'}
                </Typography>
              ) : (
                <List>
                  {/* 4 ago 2026 (pedido de Augusto): una sola fila por
                      tarjeta/deuda con 2 columnas (nacional | dólares) en
                      vez de una fila separada por cada moneda -- antes
                      "American Express" aparecía 2 veces (una fila en
                      soles, otra en dólares). */}
                  {deudasAgrupadas.map((grupo) => {
                    const pagos = [monedaLocal, monedaDolares]
                      .filter((m) => grupo.pagos[m] !== undefined)
                      .map((m) => `${getCurrencySymbol(m)}${formatAmount(grupo.pagos[m])} ${m}`)
                      .join(' / ');
                    return (
                      <ListItem key={grupo.id} divider>
                        <ListItemText
                          primary={grupo.name}
                          secondary={`${grupo.institution}${grupo.institution ? ' - ' : ''}${language === 'es' ? 'Pago mensual' : 'Monthly payment'}: ${pagos || '—'}`}
                        />
                        <Box sx={{ display: 'flex', gap: 2.5 }}>
                          <Box sx={{ textAlign: 'right', minWidth: 70 }}>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                              {monedaLocal}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: grupo.montos[monedaLocal] !== undefined ? 'error.main' : 'text.disabled' }}>
                              {grupo.montos[monedaLocal] !== undefined
                                ? `${getCurrencySymbol(monedaLocal)}${formatAmount(grupo.montos[monedaLocal])}`
                                : '—'}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right', minWidth: 70 }}>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                              {monedaDolares}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: grupo.montos[monedaDolares] !== undefined ? 'error.main' : 'text.disabled' }}>
                              {grupo.montos[monedaDolares] !== undefined
                                ? `${getCurrencySymbol(monedaDolares)}${formatAmount(grupo.montos[monedaDolares])}`
                                : '—'}
                            </Typography>
                          </Box>
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 29 ago 2026 (pedido de Augusto: "PCM tiene que ... llevar un
            tracking en el Tablero de cómo va [el presupuesto]... si la
            persona lo ve, más fácil le es cambiar"): resumen de las
            categorías más cerca de su límite mensual, justo donde el
            cliente ya revisa sus finanzas cada vez que entra. El detalle
            completo (editar montos, agregar categorías) vive en
            /budgets -- ver BudgetProgressCards.js. */}
        <Grid item xs={12}>
          <BudgetProgressCards />
        </Grid>
      </Grid>
    </Container>
  );
};

export default NotionDashboard;
